import * as _ from "lodash";
import * as angular from "angular";
import { CheckManager } from "../../shared/check-manager";
import { Team } from "../../models/team.model";
import { NavigationBarHelperService } from "../navigation/navigation-bar.helper";
import { PermissionCheck } from "../../shared/permission";
import { UserDataProvider } from "../../data-provider/user.dp";
import { DialogUtils } from "../../helper/dialog.helper";
import { ToastUtils } from "../../helper/toast.helper";
import { TeamDataProvider } from "../../data-provider/team.dp";
import { SearchUtils } from "../../helper/search.helper";
import { QrCodeService } from "../../services/qr-code.service";

let TeamListComponent = {
    selector: "vcTeamList",
    templateUrl: "/angular/components/team/team-list.component.html",
    bindings: {},
    controller: class TeamListController {
        public filter = { search: "" };
        public checkManager: CheckManager;
        public search: any;
        public searchDelay: any;
        public defaultQrTeamId: number | null = null;

        private readonly $scope: any;
        private $interval: any;
        private $q: any;
        private $location: any;
        private readonly modelsUtils: any;
        private readonly toast: ToastUtils;
        private readonly dialog: DialogUtils;

        private userDataProvider: UserDataProvider;
        public dataProvider: TeamDataProvider;
        public permissionProvider: PermissionCheck;
        public navigationBarService: NavigationBarHelperService;
        private readonly qrCodeService: QrCodeService;

        constructor(
            $scope: any,
            $interval: any,
            $q: any,
            $location: any,
            ModelsUtils: any,
            ToastUtils: ToastUtils,
            DialogUtils: DialogUtils,
            TeamDataProvider: TeamDataProvider,
            PermissionCheck: PermissionCheck,
            NavigationBarHelperService: NavigationBarHelperService,
            UserDataProvider: UserDataProvider,
            SearchUtils: SearchUtils,
            QrCodeService: QrCodeService
        ) {
            this.$scope = $scope;
            this.$interval = $interval;
            this.$q = $q;
            this.$location = $location;
            this.toast = ToastUtils;
            this.modelsUtils = ModelsUtils;
            this.dialog = DialogUtils;

            this.dataProvider = TeamDataProvider;
            this.permissionProvider = PermissionCheck;
            this.navigationBarService = NavigationBarHelperService;
            this.userDataProvider = UserDataProvider;
            this.qrCodeService = QrCodeService;

            this.filter = { search: "" };
            this.checkManager = this.dataProvider.checkManager;
            this.search = $location.search();
            if (this.search.filter) {
                this.filter = JSON.parse(this.search.filter);
                SearchUtils.searchText = this.filter.search;
            }

            SearchUtils.setSearchBar(
                "Search Teams",
                (searchText: string): Promise<any[]> => {
                    this.filter.search = searchText;
                    return this.$q.resolve([]); //Not using the results for the search dropdown so return empty.
                },
                null,
                true
            );

            TeamDataProvider.setFilter(this.filter);
        }

        $onInit() {
            this.loadDefaultQrTeam();

            this.$scope.$watch(
                () => this.filter,
                (newValue: any, oldValue: any) => {
                    if (newValue && newValue !== oldValue) {
                        this.dataProvider.loading = true;
                        this.$location.search(
                            "filter",
                            JSON.stringify(this.filter)
                        );

                        if (this.searchDelay) {
                            this.searchDelay = this.$interval.cancel(
                                this.searchDelay
                            );
                        }
                        this.searchDelay = this.$interval(() => {
                            this.$interval.cancel(this.searchDelay);
                            this.dataProvider.setFilter(this.filter);
                        }, 2000);
                    }
                },
                true
            );
        }

        // Highlights which team is the campaign's QR sign-up default. Only
        // relevant when the QR feature is granted to this permission level.
        private loadDefaultQrTeam(): void {
            this.permissionProvider.getFeaturePermissions().then(() => {
                if (!this.permissionProvider.featureGrants("QR_SIGNUP")) {
                    return;
                }
                this.qrCodeService.getSettings().then(
                    (res: any) => {
                        this.defaultQrTeamId = res.data.data.team_id ?? null;
                    },
                    () => this.toast.error()
                );
            });
        }

        isDefaultQrTeam(team: Team): boolean {
            return (
                this.defaultQrTeamId != null &&
                Number(team.tid) === Number(this.defaultQrTeamId)
            );
        }

        scrollLoad(lastTeam: Team): void {
            if (!this.dataProvider.scrollLoading) {
                this.dataProvider.getFilteredTeams(lastTeam, true);
            }
        }
        activateTeam(selectedTeam: Team, ev: any): void {
            this.dataProvider.getTeam(selectedTeam.tid).then((team: Team) => {
                const users = team.users.filter(
                    (item: any) => item.seated == "0"
                );
                const invites = team.invites.filter(
                    (item: any) => item.seated == "0"
                );

                this.userDataProvider
                    .confirmActivateDialog(users, invites, ev)
                    .then(() => this.dataProvider.resetItems());
            });
        }

        deactivateTeam(selectedTeam: Team, ev: any): void {
            this.dataProvider.getTeam(selectedTeam.tid).then((team: Team) => {
                const users = team.users.filter((item: any) => {
                    return item.seated == "1";
                });
                const invites = team.invites.filter((item: any) => {
                    return item.seated == "1";
                });

                this.userDataProvider
                    .deactivateUsers(users, invites, ev)
                    .then(() => this.dataProvider.resetItems());
            });
        }

        openEditTeamDialog(team: Team, ev: any): void {
            this.dataProvider
                .getTeam(team.tid, this.filter)
                .then((team: Team) => {
                    if (team.users.length) {
                        team.users.forEach((user: any, index: number) => {
                            team.users[index] = this.modelsUtils.user(user);
                        });
                    }
                    this.dialog
                        .show({
                            component: "vc-team-form",
                            targetEvent: ev,
                            scope: this.$scope,
                            size: "large-plus",
                            ariaLabel: "Edit Team",
                            locals: {
                                selectedTeam: team,
                                hideUsers: false,
                            },
                        })
                        .then((newTeam: Team) => {
                            if (team && newTeam) {
                                this.dataProvider.resetItems();
                                // The team form may have changed the campaign's
                                // QR default team; re-fetch so the badge updates
                                // without a page refresh.
                                this.loadDefaultQrTeam();
                            }
                        });
                });
        }

        bulk_delete(ev: any): void {
            const count = _.cloneDeep(this.checkManager.checked_items_count);
            this.dialog
                .delete({
                    targetEvent: ev,
                    skipHide: true,
                    fullscreen: true,
                    locals: {
                        title: `Delete ${count} ${
                            count > 1 ? "Teams" : "Team"
                        }?`,
                        bodyText: "They will be permanently deleted",
                    },
                })
                .then(() => {
                    this.dataProvider
                        .bulkDelete(this.checkManager.checked_items)
                        .then(
                            () => {
                                this.userDataProvider.resetItems();
                                this.toast.success(count + " teams deleted");
                            },
                            () => this.toast.error()
                        );
                });
        }

        deleteTeam(team: Team, ev: any): void {
            this.dialog
                .delete({
                    targetEvent: ev,
                    skipHide: true,
                    fullscreen: true,
                    locals: {
                        title: `Delete Team?`,
                        bodyText: "They will be permanently deleted",
                    },
                })
                .then(() => {
                    this.dataProvider.deleteTeam(team).then(
                        () => {
                            this.dataProvider.resetItems();
                            this.toast.success("Team deleted");
                        },
                        () => this.toast.error()
                    );
                });
        }

        openTeamScoreBoard(team: Team): void {
            this.dataProvider.checkManager.checked_items = [];
            this.$location.url("/teams/scoreboard/" + team.tid);
        }
    },
};

angular.module("team").component(TeamListComponent.selector, TeamListComponent);
